import axios from 'axios';
import { emitToast } from '../context/ToastContext';
import { API_BASE } from '../config/runtimeConfig';
import { captureApiFailure } from '../observability';
import { getFriendlyErrorMessage, getRawErrorDetail } from '../utils/errorMessages';

// Persist token in sessionStorage (cleared on tab close — safer than localStorage)
const getStoredToken = () => {
  try { return sessionStorage.getItem('su_access_token'); } catch { return null; }
};

// Correlation id — ties a support case to the backend log / Sentry event.
// Native crypto.randomUUID (guarded); falls back to a timestamp-random id.
const newCorrelationId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* crypto unavailable */ }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};
export const setStoredToken = (token) => {
  try { if (token) sessionStorage.setItem('su_access_token', token); else sessionStorage.removeItem('su_access_token'); } catch { /* sessionStorage unavailable */ }
};

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach stored token to every request
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Set a fresh correlation id unless the caller already provided one.
  if (!config.headers['X-Correlation-Id']) {
    config.headers['X-Correlation-Id'] = newCorrelationId();
  }
  return config;
});

// Add a response interceptor to handle errors globally
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Prefer the backend's echoed id; fall back to the one we sent on this request.
    const correlationId =
      error.response?.headers?.['x-correlation-id'] ||
      error.config?.headers?.['X-Correlation-Id'];
    error.correlationId = correlationId;

    // Capture the RAW server cause (status + un-sanitized message) for operators.
    // Kept off the customer-facing friendly text; secret-looking fields stripped.
    error.rawDetail = getRawErrorDetail(error);

    // Report API failure to Sentry (best-effort, never throws)
    captureApiFailure({
      method: error.config?.method,
      path: error.config?.url,
      status,
      correlationId,
      message: error.response?.data?.message || error.response?.data?.title || error.message,
    });

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      setStoredToken(null);
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?expired=true';
      }

      return Promise.reject(error);
    }

    const message = getFriendlyErrorMessage(error);
    error.normalizedMessage = message;

    // Operator-facing copy of the message with a subtle, copyable reference id.
    // Friendly customer-safe text is unchanged; the "Ref:" line lets an operator
    // link this case to the backend log / Sentry event.
    const operatorMessage = correlationId ? `${message}\nRef: ${correlationId}` : message;

    // Operator-only raw detail — surfaced as a subtle, expandable/copyable block
    // in the toast, secondary to the friendly text. Staff on this console see the
    // real server cause; end-customers only ever see `message`.
    const detail = {
      status,
      raw: error.rawDetail?.message || null,
      correlationId,
    };

    if (status >= 400 && status !== 401) {
      emitToast({ type: 'error', message: operatorMessage, detail });
    } else if (!error.response && error.request) {
      emitToast({ type: 'error', message: operatorMessage, detail });
    }

    return Promise.reject(error);
  }
);

export default api;
